import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { telecmiApi, usersApi } from "../lib/api";
import { toast } from "sonner";
import {
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  PhoneOff,
  Play,
  Loader2,
  Settings,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Users,
  Sparkles,
  Plus,
  Trash2,
  Search,
  Headphones,
  Copy,
  BarChart3,
  TrendingUp,
  Users2,
  MessageCircle,
  CreditCard,
  QrCode,
  Mail,
  Activity,
  X,
} from "lucide-react";
import Customer360Panel from "../components/Customer360Panel";

const fmt = (sec) => {
  const s = Number(sec || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const StatusPill = ({ status }) => {
  const s = (status || "").toLowerCase();
  const map = {
    answered: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "Answered" },
    completed: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "Completed" },
    missed: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", label: "Missed" },
    no_answer: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", label: "No answer" },
    failed: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", label: "Failed" },
    initiated: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", label: "Initiated" },
    progress: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", label: "In progress" },
  };
  const cfg = map[s] || { bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200", label: status || "—" };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border} uppercase tracking-wider`}>
      {cfg.label}
    </span>
  );
};

const DirectionIcon = ({ dir }) => {
  if (dir === "inbound") return <PhoneIncoming className="w-3.5 h-3.5 text-emerald-600" />;
  if (dir === "outbound") return <PhoneOutgoing className="w-3.5 h-3.5 text-blue-600" />;
  if (dir === "missed") return <PhoneMissed className="w-3.5 h-3.5 text-red-600" />;
  return <Phone className="w-3.5 h-3.5 text-gray-500" />;
};

// ======================= TELECMI STATUS BANNER =======================
const StatusBanner = ({ status, onRefresh }) => {
  if (!status) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center gap-3" data-testid="telecmi-status-loading">
        <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
        <p className="text-sm text-gray-600">Loading Telecmi status…</p>
      </div>
    );
  }

  const ok = status.configured;
  if (ok) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3" data-testid="telecmi-status-banner-ok">
        <div className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-emerald-900">Telecmi connected</p>
          <p className="text-[12px] text-emerald-700 mt-0.5">
            App ID <code className="bg-white/60 px-1 rounded">{status.app_id}</code> ·
            DID <code className="bg-white/60 px-1 rounded ml-1">{status.did}</code>
          </p>
        </div>
        <button onClick={onRefresh} className="text-[11px] text-emerald-700 hover:underline font-bold" data-testid="telecmi-refresh-btn">
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="telecmi-status-banner-missing">
      <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
        <AlertTriangle className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-bold text-amber-900">Telecmi not configured</p>
        <p className="text-[12px] text-amber-700 mt-0.5">
          Add <code className="bg-white/60 px-1 rounded">TELECMI_APP_ID</code>,{" "}
          <code className="bg-white/60 px-1 rounded">TELECMI_APP_SECRET</code>, and{" "}
          <code className="bg-white/60 px-1 rounded">TELECMI_DID</code> to backend/.env.
        </p>
      </div>
    </div>
  );
};

// ======================= WEBHOOK URLS CARD =======================
const WebhookCard = ({ status }) => {
  if (!status?.webhook_url) return null;
  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copied`));
  };
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="telecmi-webhook-card">
      <div className="flex items-center gap-2 mb-3">
        <Settings className="w-4 h-4 text-gray-500" />
        <h3 className="text-sm font-bold">Webhook URLs to paste in your PIOPIY dashboard</h3>
      </div>
      <p className="text-[12px] text-gray-600 leading-relaxed mb-4">
        In <a href="https://piopiy.telecmi.com" target="_blank" rel="noreferrer" className="text-orange-600 underline">PIOPIY dashboard</a> → Settings → Webhooks, paste these URLs.
      </p>
      <div className="space-y-2">
        <div className="bg-gray-50 rounded-lg p-2.5 flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 w-24 flex-shrink-0">CDR</span>
          <code className="text-[11px] text-gray-800 flex-1 truncate">{status.webhook_url}</code>
          <button onClick={() => copy(status.webhook_url, "Webhook URL")} className="p-1 hover:bg-white rounded" title="Copy">
            <Copy className="w-3.5 h-3.5 text-gray-500" />
          </button>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5 flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 w-24 flex-shrink-0">Answer URL</span>
          <code className="text-[11px] text-gray-800 flex-1 truncate">{status.answer_url}</code>
          <button onClick={() => copy(status.answer_url, "Answer URL")} className="p-1 hover:bg-white rounded">
            <Copy className="w-3.5 h-3.5 text-gray-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ======================= AGENT MAPPING CARD =======================
const AgentMappingCard = ({ user }) => {
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [mappings, setMappings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newUserId, setNewUserId] = useState("");
  const [newExt, setNewExt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [mr, ur] = await Promise.all([telecmiApi.listAgents(), usersApi.getAll()]);
      setMappings(mr.data?.agents || []);
      const all = ur.data?.users || ur.data || [];
      setUsers(Array.isArray(all) ? all : []);
    } catch (e) {
      toast.error("Could not load agent mappings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin]);

  const userMap = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  const submit = async () => {
    if (!newUserId || !newExt.trim()) {
      toast.error("Pick an agent and enter an extension");
      return;
    }
    setBusy(true);
    try {
      await telecmiApi.upsertAgent({ user_id: newUserId, extension: newExt.trim() });
      toast.success("Mapping saved");
      setShowNew(false);
      setNewUserId("");
      setNewExt("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId) => {
    if (!window.confirm("Remove this Telecmi extension mapping?")) return;
    try {
      await telecmiApi.deleteAgent(userId);
      toast.success("Mapping removed");
      await load();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  if (!isAdmin) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-gray-500" />
          <h3 className="text-sm font-bold">Agent extensions</h3>
        </div>
        <p className="text-[12px] text-gray-600">Only admins can manage agent ↔ extension mappings.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="telecmi-agents-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-500" />
          <h3 className="text-sm font-bold">Agent ↔ Telecmi extension mappings</h3>
        </div>
        <button
          onClick={() => setShowNew((v) => !v)}
          className="text-[11px] font-bold bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 px-2.5 py-1 rounded-full flex items-center gap-1"
          data-testid="telecmi-add-mapping-btn"
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <p className="text-[12px] text-gray-600 mb-4">
        Map each CRM user to their Telecmi SIP extension so Click-to-Call rings them. Without a mapping, the user can't make calls.
      </p>

      {showNew && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-3" data-testid="telecmi-new-mapping-row">
          <div className="grid grid-cols-12 gap-2">
            <select
              className="col-span-7 text-xs border border-orange-200 rounded-lg px-2 py-1.5 bg-white"
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
              data-testid="telecmi-mapping-user-select"
            >
              <option value="">Pick an agent…</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} · {u.email} · {u.role}
                </option>
              ))}
            </select>
            <input
              className="col-span-3 text-xs border border-orange-200 rounded-lg px-2 py-1.5 bg-white"
              placeholder="ext (e.g. 1001)"
              value={newExt}
              onChange={(e) => setNewExt(e.target.value)}
              data-testid="telecmi-mapping-ext-input"
            />
            <button
              onClick={submit}
              disabled={busy}
              className="col-span-2 text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white rounded-lg disabled:opacity-50"
              data-testid="telecmi-mapping-save-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : "Save"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-[12px] text-gray-500 py-4 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : mappings.length === 0 ? (
        <div className="text-center py-6 text-[12px] text-gray-500">
          No mappings yet. Add one above to enable Click-to-Call.
        </div>
      ) : (
        <div className="space-y-2">
          {mappings.map((m) => {
            const u = userMap.get(m.user_id);
            return (
              <div key={m.user_id} className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-xs">
                  {(u?.name || m.display_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{u?.name || m.display_name}</p>
                  <p className="text-[10px] text-gray-500 truncate">{u?.email || m.email} · {u?.role || "—"}</p>
                </div>
                <span className="text-[11px] font-mono bg-white border border-gray-200 px-2 py-0.5 rounded">EXT {m.extension}</span>
                <button onClick={() => remove(m.user_id)} className="p-1.5 hover:bg-red-50 rounded text-red-500" title="Remove" data-testid={`telecmi-remove-${m.user_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ======================= CLICK-TO-CALL DEMO =======================
const ClickToCallDemo = () => {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const submit = async () => {
    if (!phone.trim()) {
      toast.error("Enter a phone number");
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const res = await telecmiApi.clickToCall(phone, null);
      toast.success("Call initiated");
      setLastResult({ ok: true, body: res.data });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(detail);
      setLastResult({ ok: false, error: detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="telecmi-click-demo">
      <div className="flex items-center gap-2 mb-3">
        <PhoneCall className="w-4 h-4 text-gray-500" />
        <h3 className="text-sm font-bold">Click-to-Call quick test</h3>
      </div>
      <p className="text-[12px] text-gray-600 mb-3">
        Place a test call to any phone number. You must have a Telecmi extension mapping for your account.
      </p>
      <div className="flex gap-2 mb-2">
        <input
          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-orange-300"
          placeholder="+91 98765 43210"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          data-testid="telecmi-demo-phone-input"
        />
        <button
          onClick={submit}
          disabled={busy}
          className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
          data-testid="telecmi-demo-call-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
          Call
        </button>
      </div>
      {lastResult && (
        <div className={`text-[11px] rounded-lg p-2.5 mt-2 ${lastResult.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {lastResult.ok ? (
            <>
              <CheckCircle2 className="inline w-3 h-3 mr-1" /> Call queued. Watch the call log below for status updates.
            </>
          ) : (
            <>
              <AlertTriangle className="inline w-3 h-3 mr-1" /> {lastResult.error}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ======================= CALL LOG TABLE =======================
const CallLogTable = ({ refreshKey }) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [c360Phone, setC360Phone] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await telecmiApi.listCalls({ limit: 100 });
      setCalls(r.data?.calls || []);
    } catch (e) {
      toast.error("Could not load call log");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [refreshKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return calls;
    return calls.filter((c) =>
      [c.from_number, c.to_number, c.agent_name, c.status, c.direction]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q))
    );
  }, [calls, search]);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="telecmi-call-log">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Headphones className="w-4 h-4 text-gray-500" />
          <h3 className="text-sm font-bold">Recent calls</h3>
          <span className="text-[10px] text-gray-500">{filtered.length} of {calls.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="text-xs border border-gray-200 rounded-full pl-7 pr-3 py-1 outline-none focus:border-orange-300"
              data-testid="telecmi-call-search"
            />
          </div>
          <button onClick={load} className="text-[11px] font-bold text-gray-600 hover:text-orange-600">
            Refresh
          </button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-[12px] text-gray-500 py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 px-6">
          <Phone className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-bold text-gray-700">No calls yet</p>
          <p className="text-[12px] text-gray-500 mt-1">Place a click-to-call test, or wait for inbound calls to land here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">Direction</th>
                <th className="px-4 py-2 text-left">Customer</th>
                <th className="px-4 py-2 text-left">Agent</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Duration</th>
                <th className="px-4 py-2 text-left">Recording</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const customerNum = c.direction === "outbound" ? c.to_number : c.from_number;
                return (
                  <tr
                    key={c.id}
                    className="border-t border-gray-100 hover:bg-orange-50/30 cursor-pointer"
                    onClick={() => customerNum && setC360Phone(customerNum)}
                    data-testid={`call-row-${c.id}`}
                  >
                    <td className="px-4 py-2 text-[11px] text-gray-700">{fmtTime(c.created_at || c.completed_at)}</td>
                    <td className="px-4 py-2"><DirectionIcon dir={c.direction} /></td>
                    <td className="px-4 py-2 text-[12px] font-mono">{customerNum || "—"}</td>
                    <td className="px-4 py-2 text-[12px]">{c.agent_name || c.agent_extension || "—"}</td>
                    <td className="px-4 py-2"><StatusPill status={c.status} /></td>
                    <td className="px-4 py-2 text-[11px] text-gray-600">{c.duration ? fmt(c.duration) : "—"}</td>
                    <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                      {c.recording_url ? (
                        <a href={c.recording_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-bold text-orange-600 hover:underline">
                          <Play className="w-3 h-3" /> Play
                        </a>
                      ) : (
                        <span className="text-[10px] text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {c360Phone && (
        <Customer360Panel phone={c360Phone} onClose={() => setC360Phone(null)} mode="drawer" />
      )}
    </div>
  );
};

// ======================= ANALYTICS DASHBOARD =======================
const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await telecmiApi.analytics(d);
      setData(r.data);
    } catch (e) {
      toast.error("Could not load analytics");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(days); }, [days]);

  if (loading || !data) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-12 flex items-center justify-center" data-testid="telecmi-analytics-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const k = data.kpis || {};
  const fmt2 = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const maxHourly = Math.max(...(data.hourly || [1]), 1);
  const dispoColors = {
    sale_closed: "bg-emerald-500",
    follow_up: "bg-amber-500",
    info_given: "bg-blue-500",
    refund_cancel: "bg-red-500",
    wrong_number: "bg-gray-400",
    voicemail: "bg-gray-400",
    unknown: "bg-gray-300",
  };
  const totalDispo = (data.disposition_breakdown || []).reduce((s, d) => s + d.count, 0) || 1;
  const maxLeader = Math.max(...((data.leaderboard || []).map(a => a.calls)), 1);

  return (
    <div className="space-y-4" data-testid="telecmi-analytics-dashboard">
      {/* KPI cards */}
      <div className="grid grid-cols-5 gap-3">
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-4 border border-orange-200">
          <p className="text-[10px] font-bold text-orange-700 uppercase tracking-wider mb-1">Total Calls</p>
          <p className="text-3xl font-extrabold">{k.total_calls || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-4 border border-emerald-200">
          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1">Connect Rate</p>
          <p className="text-3xl font-extrabold">{k.connect_rate || 0}<span className="text-lg">%</span></p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4 border border-blue-200">
          <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider mb-1">Avg Talk Time</p>
          <p className="text-3xl font-extrabold">{fmt2(k.avg_talk_seconds || 0)}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-4 border border-red-200">
          <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider mb-1">Missed</p>
          <p className="text-3xl font-extrabold">{k.missed || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-4 border border-purple-200">
          <p className="text-[10px] font-bold text-purple-700 uppercase tracking-wider mb-1">Total Talk</p>
          <p className="text-3xl font-extrabold">{Math.round((k.total_talk_seconds || 0) / 60)}<span className="text-lg">m</span></p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Leaderboard */}
        <div className="col-span-7 bg-white border border-gray-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-sm flex items-center gap-2">
              <Users2 className="w-4 h-4 text-orange-500" /> Calls by Agent
            </h4>
            <select className="text-[11px] border border-gray-200 rounded px-2 py-1" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value="1">Today</option>
              <option value="7">Last 7d</option>
              <option value="30">Last 30d</option>
              <option value="90">Last 90d</option>
            </select>
          </div>
          {data.leaderboard?.length > 0 ? (
            <div className="space-y-2.5">
              {data.leaderboard.map((a, i) => (
                <div key={a.id || i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[9px] flex items-center justify-center font-bold">
                        {(a.name || "?").charAt(0).toUpperCase()}
                      </span>
                      {a.name || "Unassigned"}
                    </span>
                    <span className="text-[10px] text-gray-500">{a.calls} calls · {a.answered} answered · {a.sales} sales</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-400 to-red-500 rounded-full" style={{ width: `${(a.calls / maxLeader) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[12px] text-gray-500 py-6 text-center">No calls in this period yet.</p>
          )}
        </div>

        {/* Disposition + heatmap */}
        <div className="col-span-5 space-y-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-5">
            <h4 className="font-bold text-sm flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-orange-500" /> Disposition mix
            </h4>
            {data.disposition_breakdown?.length > 0 ? (
              <div className="space-y-2">
                {data.disposition_breakdown.slice(0, 6).map((d) => (
                  <div key={d.disposition}>
                    <div className="flex items-center justify-between text-[11px] mb-0.5">
                      <span className="font-semibold capitalize">{(d.disposition || "unknown").replace(/_/g, " ")}</span>
                      <span className="font-bold text-gray-700">{d.count} · {Math.round((d.count / totalDispo) * 100)}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full ${dispoColors[d.disposition] || "bg-gray-300"}`} style={{ width: `${(d.count / totalDispo) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[12px] text-gray-500 py-3 text-center">Disposition data appears once AI summaries run.</p>
            )}
          </div>

          {/* Hourly heatmap */}
          <div className="bg-white border border-gray-200 rounded-2xl p-5">
            <h4 className="font-bold text-sm flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-orange-500" /> When customers call
              <span className="text-[10px] text-gray-400 font-normal">(IST)</span>
            </h4>
            <div className="flex items-end gap-1 h-20">
              {(data.hourly || []).map((count, hr) => {
                const h = (count / maxHourly) * 100;
                return (
                  <div key={`hr-${hr}`} className="flex-1 flex flex-col items-center gap-1" title={`${hr}:00 — ${count} calls`}>
                    <div
                      className="w-full bg-gradient-to-t from-orange-400 to-orange-300 rounded-t"
                      style={{ height: `${Math.max(h, 4)}%` }}
                    />
                    <span className="text-[8px] text-gray-400 font-bold">{hr % 3 === 0 ? hr : ""}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ======================= SPRINT 2: WALLBOARD =======================
const Wallboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await telecmiApi.wallboard();
      setData(r.data);
    } catch (e) {
      // 403 — not a leader
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  if (loading || !data) return null;
  if (!data.rows?.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-5 text-center text-[12px] text-gray-500" data-testid="telecmi-wallboard-empty">
        No agent extension mappings yet. Add some in the Overview tab to populate the wallboard.
      </div>
    );
  }
  const s = data.summary;
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="telecmi-wallboard">
      <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h4 className="text-sm font-bold flex items-center gap-2">
          <Users2 className="w-4 h-4 text-orange-500" /> Live Wallboard
          <span className="text-[10px] bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded-full uppercase">Live · auto-refresh 10s</span>
        </h4>
        <div className="flex gap-2 text-[11px]">
          <span className="font-bold text-emerald-700">{s.idle} idle</span>
          <span className="text-gray-300">·</span>
          <span className="font-bold text-red-700">{s.on_call} on call</span>
          <span className="text-gray-300">·</span>
          <span className="font-bold text-gray-700">{s.today_calls_total} calls today</span>
        </div>
      </div>
      <div className="divide-y divide-gray-100">
        {data.rows.map((r) => {
          const onCall = r.state === "on_call";
          return (
            <div key={r.user_id} className={`flex items-center gap-3 px-4 py-2.5 ${onCall ? "bg-red-50/40" : ""}`}>
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white flex items-center justify-center font-bold text-sm">
                  {(r.name || "?").charAt(0).toUpperCase()}
                </div>
                <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${onCall ? "bg-red-500 animate-pulse" : "bg-emerald-500"}`}></span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{r.name}</p>
                <p className="text-[10px] text-gray-500">
                  {r.department || "—"} · ext <span className="font-mono font-bold">{r.extension}</span>
                  {onCall && r.active_call ? (
                    <span className="text-red-600 font-bold ml-2">
                      ● On call ({r.active_call.direction || "?"} {r.active_call.from_number || r.active_call.to_number})
                    </span>
                  ) : null}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-bold">{r.today_answered}/{r.today_calls}</p>
                <p className="text-[9px] text-gray-500 uppercase">today</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ======================= SPRINT 2: OUTBOUND QUEUE =======================
const OutboundQueueCard = ({ user }) => {
  const isLeader = user?.role === "admin" || user?.role === "super_admin" || user?.role === "leader";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ customer_phone: "", customer_name: "", note: "", priority: "normal" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await telecmiApi.getQueue(false);
      setItems(r.data?.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.customer_phone) { toast.error("Phone required"); return; }
    try {
      await telecmiApi.addQueue(form);
      toast.success("Added to outbound queue");
      setForm({ customer_phone: "", customer_name: "", note: "", priority: "normal" });
      setShowAdd(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const callItem = async (item) => {
    try {
      await telecmiApi.clickToCall(item.customer_phone, item.lead_id);
      toast.success(`Calling ${item.customer_name || item.customer_phone}…`);
      await telecmiApi.completeQueue(item.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not call");
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="telecmi-outbound-queue">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <h4 className="text-sm font-bold flex items-center gap-2">
          <Phone className="w-4 h-4 text-orange-500" /> Outbound Call Queue
          <span className="text-[10px] text-gray-500">· {items.length} pending</span>
        </h4>
        {isLeader && (
          <button onClick={() => setShowAdd(v => !v)} className="text-[11px] font-bold bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 px-2.5 py-1 rounded-full flex items-center gap-1" data-testid="add-outbound-btn">
            <Plus className="w-3 h-3" /> Add
          </button>
        )}
      </div>
      {showAdd && (
        <div className="p-3 bg-orange-50 border-b border-orange-200">
          <div className="grid grid-cols-12 gap-2">
            <input className="col-span-3 text-xs border border-orange-200 rounded-lg px-2 py-1.5 bg-white" placeholder="Phone *" value={form.customer_phone} onChange={(e) => setForm({...form, customer_phone: e.target.value})} />
            <input className="col-span-3 text-xs border border-orange-200 rounded-lg px-2 py-1.5 bg-white" placeholder="Name" value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} />
            <input className="col-span-4 text-xs border border-orange-200 rounded-lg px-2 py-1.5 bg-white" placeholder="Note" value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} />
            <button onClick={submit} className="col-span-2 text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white rounded-lg">Save</button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" /></div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-[12px] text-gray-500">
          Queue is empty. {isLeader ? "Add callbacks above or use power-dial from order list." : "Your leader hasn't assigned any callbacks yet."}
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {items.slice(0, 20).map((it) => (
            <div key={it.id} className="px-4 py-2 flex items-center gap-3 hover:bg-gray-50">
              <div className={`w-1.5 h-8 rounded-full ${it.priority === "urgent" ? "bg-red-500" : it.priority === "low" ? "bg-gray-300" : "bg-amber-400"}`}></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{it.customer_name || it.customer_phone}</p>
                <p className="text-[10px] text-gray-500 truncate">{it.note || "—"}</p>
              </div>
              <span className="text-[10px] font-mono text-gray-400">{it.customer_phone}</span>
              <button onClick={() => callItem(it)} className="bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1" data-testid={`call-queue-${it.id}`}>
                <Phone className="w-3 h-3" /> Call
              </button>
              {isLeader && (
                <button onClick={async () => { await telecmiApi.removeQueue(it.id); await load(); }} className="text-gray-400 hover:text-red-500 p-1">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ======================= SPRINT 2: CALLBACKS =======================
const CallbacksCard = ({ user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await telecmiApi.getCallbacks();
      setItems(r.data?.callbacks || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    await telecmiApi.completeCallback(id);
    await load();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="telecmi-callbacks">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <h4 className="text-sm font-bold flex items-center gap-2">
          <Clock className="w-4 h-4 text-orange-500" /> Callback Reminders
          <span className="text-[10px] text-gray-500">· {items.length} pending</span>
        </h4>
      </div>
      {loading ? (
        <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" /></div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-[12px] text-gray-500">
          No callbacks scheduled. AI auto-creates them from "I'll call you Monday" mentions in calls.
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {items.map((c) => (
            <div key={c.id} className="px-4 py-2 flex items-center gap-3 hover:bg-gray-50">
              <Clock className="w-3.5 h-3.5 text-amber-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{c.customer_name || c.phone}</p>
                <p className="text-[10px] text-gray-500 truncate">
                  {c.scheduled_at ? new Date(c.scheduled_at).toLocaleString("en-IN", {dateStyle: "medium", timeStyle: "short"}) : "—"} · {c.note || ""}
                </p>
              </div>
              <button onClick={async () => {
                try { await telecmiApi.clickToCall(c.phone, c.lead_id); toast.success("Calling…"); await complete(c.id); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }} className="bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1">
                <Phone className="w-3 h-3" /> Call now
              </button>
              <button onClick={() => complete(c.id)} className="text-gray-400 hover:text-emerald-600 p-1" title="Mark done">
                <CheckCircle2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ======================= SPRINT 2: VOICEMAIL DROPS =======================
const VoicemailsCard = ({ user }) => {
  const isLeader = user?.role === "admin" || user?.role === "super_admin" || user?.role === "leader";
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", audio_url: "", description: "" });

  const load = async () => {
    try {
      const r = await telecmiApi.listVoicemails();
      setItems(r.data?.voicemails || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.audio_url) { toast.error("Name + audio URL required"); return; }
    await telecmiApi.createVoicemail({ ...form, is_active: true });
    toast.success("Voicemail saved");
    setForm({ name: "", audio_url: "", description: "" });
    setShowAdd(false);
    await load();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="telecmi-voicemails">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <h4 className="text-sm font-bold flex items-center gap-2">
          <Headphones className="w-4 h-4 text-orange-500" /> Voicemail Drops
        </h4>
        {isLeader && (
          <button onClick={() => setShowAdd(v => !v)} className="text-[11px] font-bold bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 px-2.5 py-1 rounded-full flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add
          </button>
        )}
      </div>
      {showAdd && (
        <div className="p-3 bg-orange-50 border-b border-orange-200">
          <div className="space-y-2">
            <input className="w-full text-xs border border-orange-200 rounded-lg px-2 py-1.5 bg-white" placeholder="Name (e.g. 'We'll call back')" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
            <input className="w-full text-xs border border-orange-200 rounded-lg px-2 py-1.5 bg-white" placeholder="Audio URL (https://...)" value={form.audio_url} onChange={(e) => setForm({...form, audio_url: e.target.value})} />
            <input className="w-full text-xs border border-orange-200 rounded-lg px-2 py-1.5 bg-white" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
            <button onClick={submit} className="text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-3 py-1.5">Save</button>
          </div>
        </div>
      )}
      {items.length === 0 ? (
        <div className="p-6 text-center text-[12px] text-gray-500">
          No voicemail templates. Upload pre-recorded MP3s and use them on missed calls.
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {items.map((v) => (
            <div key={v.id} className="px-4 py-2 flex items-center gap-3 hover:bg-gray-50">
              <Headphones className="w-3.5 h-3.5 text-blue-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{v.name}</p>
                <p className="text-[10px] text-gray-500 truncate">{v.description || v.audio_url}</p>
              </div>
              <a href={v.audio_url} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-orange-600 hover:underline">
                Preview
              </a>
              {isLeader && (
                <button onClick={async () => { await telecmiApi.removeVoicemail(v.id); await load(); }} className="text-gray-400 hover:text-red-500 p-1">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ======================= MAIN PAGE =======================
const CallsPage = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState("overview"); // overview | analytics

  const loadStatus = async () => {
    try {
      const r = await telecmiApi.status();
      setStatus(r.data);
    } catch (e) {
      // Salesperson hits 403 — that's fine, hide the admin-only banner
      setStatus({ configured: false, _no_access: true });
    }
  };
  useEffect(() => {
    loadStatus();
  }, []);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="calls-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2">
            <PhoneCall className="w-6 h-6 text-orange-500" />
            Calls
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Telecmi click-to-call, agent extensions, AI summaries, and analytics</p>
        </div>
        {/* Tab toggle */}
        <div className="bg-gray-100 rounded-full p-1 flex">
          <button
            onClick={() => setTab("overview")}
            className={`px-4 py-1.5 text-xs font-bold rounded-full transition-colors ${tab === "overview" ? "bg-white text-orange-600 shadow" : "text-gray-600 hover:text-gray-900"}`}
            data-testid="calls-tab-overview"
          >
            Overview
          </button>
          <button
            onClick={() => setTab("operations")}
            className={`px-4 py-1.5 text-xs font-bold rounded-full transition-colors flex items-center gap-1 ${tab === "operations" ? "bg-white text-orange-600 shadow" : "text-gray-600 hover:text-gray-900"}`}
            data-testid="calls-tab-operations"
          >
            <Activity className="w-3.5 h-3.5" /> Operations
          </button>
          <button
            onClick={() => setTab("analytics")}
            className={`px-4 py-1.5 text-xs font-bold rounded-full transition-colors flex items-center gap-1 ${tab === "analytics" ? "bg-white text-orange-600 shadow" : "text-gray-600 hover:text-gray-900"}`}
            data-testid="calls-tab-analytics"
          >
            <BarChart3 className="w-3.5 h-3.5" /> Analytics
          </button>
        </div>
      </div>

      {tab === "analytics" ? (
        <AnalyticsDashboard />
      ) : tab === "operations" ? (
        <div className="space-y-4" data-testid="calls-operations-tab">
          <Wallboard />
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-7"><OutboundQueueCard user={user} /></div>
            <div className="col-span-12 lg:col-span-5"><CallbacksCard user={user} /></div>
          </div>
          <VoicemailsCard user={user} />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-12 gap-4 mb-4">
            <div className="col-span-12">
              {isAdmin && !status?._no_access && (
                <StatusBanner status={status} onRefresh={loadStatus} />
              )}
            </div>
          </div>

          {isAdmin && status?.configured && (
            <div className="grid grid-cols-12 gap-4 mb-4">
              <div className="col-span-7"><WebhookCard status={status} /></div>
              <div className="col-span-5"><ClickToCallDemo /></div>
            </div>
          )}

          {isAdmin && (
            <div className="mb-4"><AgentMappingCard user={user} /></div>
          )}

          <CallLogTable refreshKey={refreshKey} />
        </>
      )}
    </div>
  );
};

export default CallsPage;
